import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';
import FONTS from '@mlb/react-patterns/style/fonts.ts';
import Button from '@mlb/react-patterns/components/Button/button.tsx';
import { Wrapper as HeaderWrapper } from '@mlb/react-patterns/components/AnimatedModal/Header/header.style.tsx';

import ModalContainer from 'bdvp-common/src/containers/Modal';
import { useKeyPress } from 'bdvp-common/src/hooks';
import { useDictionary } from 'bdvp-common/src/hooks/config';
import { keyConstants } from 'bdvp-common/src/utils';
import Spinner from 'bdvp-common/src/components/Spinner';
import { constants as modalConstants } from 'bdvp-graphql/src/resolvers/modals';
import { Tracking } from 'bdvp-tracking';

const { TAB } = keyConstants;
const keysToTrack = [TAB];

const Modal = styled(ModalContainer)`
    &&& {
        width: 270px;
        height: auto;
        top: 50%;
        left: 50%;
        border-radius: 6px;
        transform: translate(-50%, -50%);
        font-family: ${FONTS.primaryFont};
        ${HeaderWrapper} {
            padding: 16px 14px 8px 14px;
        }
        ${media.mdmin`
            width: 320px;
        `}
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 32px;
    padding-left: 32px;
    padding-right: 32px;
`;

const Title = styled.h1`
    font-family: ${FONTS.primaryFont};
    font-size: 20px;
    font-weight: bold;
    text-align: center;
    color: ${COLORS.mlbWhite};
`;

const DeleteButton = styled(Button)`
    margin-bottom: 5px;
    margin-top: 14px;
    padding-left: 20px;
    padding-right: 20px;
`;

const CancelButton = styled(Button)`
    color: ${COLORS.mlbWhite};
`;

const { DELETE_REEL_MODAL_NAME } = modalConstants;

const DeleteModal = (props) => {

    const {
        closeModal,
        deleteReelHandler,
        loading,
    } = props;

    const {
        delete_reel_confirmation_question: deleteReelConfirmationQuestion = 'Are you sure you want to delete this reel?',
        delete_reel_confirmation: deleteReelConfirmation = 'Yes, Delete This Reel',
        cancel = 'Cancel',
    } = useDictionary();

    const firstModalNode = React.useRef(null);
    const lastModalNode = React.useRef(null);
    const keyPressCallbackProps = {
        cb: (event) => {

            if (event.target === lastModalNode.current) {

                event.preventDefault();
                firstModalNode.current.focus();

            }

        },
        shiftCB: (event) => {

            if (event.target === firstModalNode.current) {

                event.preventDefault();
                lastModalNode.current.focus();

            }

        },
    };

    useKeyPress(keysToTrack, keyPressCallbackProps);

    const onCancelButtonClickHandler = () => {

        Tracking.trackDeleteReelConfirmationModalCancelClick();
        closeModal();

    };

    const onModalCloseButtonHandler = () => {

        Tracking.trackDeleteReelConfirmationModalCloseButtonClick();
        closeModal();

    };

    const onDeleteConfirmHandler = () => {

        Tracking.trackDeleteReelConfirmationModalConfirmClick();
        deleteReelHandler();

    };

    const Content = loading 
        ? <Spinner />
        : (
            <>
                <Title>{deleteReelConfirmationQuestion}</Title>
                <DeleteButton
                    label={deleteReelConfirmation}
                    handlers={{ onClick: onDeleteConfirmHandler }}
                    size={'md'}
                />
                <CancelButton
                    handlers={{ onClick: onCancelButtonClickHandler }}
                    label={cancel}
                    shape={'hollow-borderless'}
                    size={'md'}
                    ref={lastModalNode}
                />
            </>
        );

    return (
        <Modal
            closeButtonRef={firstModalNode}
            name={DELETE_REEL_MODAL_NAME}
            overlay={true}
            handlers={{
                onClosed: onModalCloseButtonHandler,
                onOpened: () => {

                    firstModalNode.current.focus();

                },
            }}
        >
            <ContentWrapper>
                {Content}
            </ContentWrapper>
        </Modal>
    );

};

DeleteModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    deleteReelHandler: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

DeleteModal.defaultProps = {
    loading: false,
};

export default DeleteModal;
